import { useState } from "react";

import { UserInfoResponse } from "../../models/User";
import { HttpService } from "../../services/api";
import { getUserInfo, getUserStoreInfo, getUserToken, searchMemberInfoByUserId, getCustomerBdpNumber, resolveCustomerBdpNumber } from "../../services/api/auth";
import { useGlobalContext } from "../../context/GlobalContext";

export const useAuthentication = () => {
  // const [userToken, setUserToken] = useState<string | undefined>(undefined);
  const [userInfo, setUserInfo] = useState<UserInfoResponse | undefined>(undefined);
  const { dispatch } = useGlobalContext()
  const authenticateUser = async (
    options?: { prefillBdpNumber?: boolean },
  ) => {
    // Prefill the saved BDP number on initial load.
    // Set prefillBdpNumber to false during submit to preserve user edits.
    const shouldPrefillBdpNumber = options?.prefillBdpNumber ?? true;
    const redirectUrl = "/pages/print-connect-registration-form";
    let membershipType: string | null = null;
    let lngParam: string | null = null;
    
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const rawMembershipType = params.get("membership_type");
      // Normalize: only "1" (Silver) and "2" (Gold) are valid; default to "1" (Silver)
      membershipType = (rawMembershipType === "1" || rawMembershipType === "2") ? rawMembershipType : "1";
      lngParam = params.get("lng");

      console.log("[auth] ===== URL & Query Params =====");
      console.log("[auth] Current full URL:", window.location.href);
      console.log("[auth] Current search params:", window.location.search);
      console.log("[auth] All query params:", Object.fromEntries(params.entries()));
      console.log("[auth] lng param:", lngParam);
      console.log("[auth] membership_type param:", membershipType);
      console.log("[auth] redirectUrl (r=):", redirectUrl);
      console.log("[auth] ==================================");
    }

    const loginUrlParams = new URLSearchParams({ r: redirectUrl });
    if (membershipType) {
      loginUrlParams.set("membership_type", membershipType);
    }
    if (lngParam) {
      loginUrlParams.set("lng", lngParam);
    }
    const accountLoginUrl = `/Account/AccountLogin.aspx?${loginUrlParams.toString()}`;
    console.log("[auth] Built AccountLogin.aspx URL:", accountLoginUrl);
    /**
     * Attempt authentication with cookie
     */
    const userTokenResponse = await getUserToken();
    if (!userTokenResponse?.data && userTokenResponse?.error) {
      dispatch({ type: "LOG_ERROR", error: { show: false, type: "alert", displayErrorMessage: "AuthenticateUser: userTokenResponse is undefined", rawErrorMessage: userTokenResponse.error } })
      window.open(accountLoginUrl, "_self");
      return;
    }

    const [userInfoResponse, userStoreInfo] = await Promise.all([
      getUserInfo(),
      getUserStoreInfo()
    ]);

    if (userInfoResponse.data) {
      if (userInfoResponse.data.UserName.startsWith("G_")) {
        console.log("[auth] AccountLogin.aspx url", accountLoginUrl);
        window.open(accountLoginUrl, "_self");
      }
      setUserInfo(userInfoResponse.data);
      dispatch({ type: 'LOGIN_USER_INFO', userInfo: userInfoResponse.data });

      const isKiosk = userInfoResponse.data.Channel?.toLowerCase() === "kiosk" || userInfoResponse.data.Channel?.toLowerCase() === "ekiosk";
      dispatch({ type: 'SET_IS_KIOSK_USER', isKioskUser: isKiosk });

      if (shouldPrefillBdpNumber) {
        try {
          const bdpResponse = await getCustomerBdpNumber();
          const savedBdpNumber = resolveCustomerBdpNumber(bdpResponse);
          if (savedBdpNumber) {
            dispatch({ type: 'CHANGE_REGISTRATION_FORM', field: 'bdpNumber', value: savedBdpNumber });
            console.log("[auth] BDP Number prefilled on form:", savedBdpNumber);
          } else {
            console.log("[auth] BDP Number not prefilled — API returned empty/null");
          }
        } catch (error) {
          console.error("[auth] Error fetching customer BDP number:", error);
        }
      } 

      // Silver member duplicate check: if member already exists, redirect to homepage
      if (membershipType === "1") {
        try {
          const memberSearchResponse = await searchMemberInfoByUserId(userInfoResponse.data.UserId);
          const memberExists = memberSearchResponse.data?.MemberExisted === true;
          console.log("[auth] Silver member check result:", {
            memberExists,
            dataType: typeof memberSearchResponse.data,
          });

          if (memberExists) {
            console.log("[auth] Silver member already exists, redirecting to PrintConnect homepage");
            const locale = lngParam?.toLowerCase().startsWith("fr") ? "fr-CA" : "en-CA";
            window.location.href = `${process.env.REACT_APP_PRINT_CONNECT_URL ?? "/pages/print-connect"}?lng=${locale}`;
            return;
          }
        } catch (error) {
          console.error("[auth] Error checking Silver member existence:", error);
          // If the check fails, allow the form to load (non-blocking)
        }
      }
    } else if (typeof window !== "undefined") {
      dispatch({ type: "LOG_ERROR", error: { show: false, type: "alert", displayErrorMessage: "AuthenticateUser: userInfoResponse is undefined", rawErrorMessage: userInfoResponse } })
      console.log("[auth] AccountLogin.aspx url", accountLoginUrl);
      window.open(accountLoginUrl, "_self");
    }

    if (typeof window !== "undefined") {
      console.log("[auth] ===== Post-Auth Check =====");
      console.log("[auth] redirectUrl (r=):", redirectUrl);
      console.log("[auth] membership_type:", membershipType);
      console.log("[auth] lng:", lngParam);
      console.log("[auth] ==================================");
    }

    if (userStoreInfo === null) {
      dispatch({ type: "CURRENT_STORE_NUMBER", currentStoreLocationNumber: 'S999' })
    } else {
      const storeId = userStoreInfo.StoreId === '' ? 'S330' : userStoreInfo.StoreId;
      dispatch({ type: "CURRENT_STORE_NUMBER", currentStoreLocationNumber: storeId })
    }

    // setUserToken(userTokenResponse.data ?? "failed-to-load-user-token");
    HttpService.init(userTokenResponse?.data ?? "failed-to-load-user-token");
  };

  const refreshCustomerBdpNumber = async (): Promise<string> => {
    try {
      const bdpResponse = await getCustomerBdpNumber();
      const savedBdpNumber = resolveCustomerBdpNumber(bdpResponse);
      dispatch({ type: "CHANGE_REGISTRATION_FORM", field: "bdpNumber", value: savedBdpNumber });
      return savedBdpNumber;
    } catch (error) {
      console.error("[auth] Error fetching customer BDP number:", error);
      return "";
    }
  };

  return {
    userInfo,
    authenticateUser,
    refreshCustomerBdpNumber,
  };
};
